/**
 * Coverage dedupe and shared TOC/primitives.
 * Soft-extract from Utils — Utils delegates here for backward compatibility.
 *
 * @file coverageDuplicationUtils.js
 */

import { COVERAGE_DEDUPE_ACTION } from '../constants';

/* ------------------------------------------------------------------
 * Internal helpers (module-private)
 * ------------------------------------------------------------------ */


/* --- TOC / tree --- */

export function findNodeById(nodes, targetId) {
  if (!Array.isArray(nodes)) return null;

  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (node.id === targetId) return node;
    if (node.children?.length) {
      const found = findNodeById(node.children, targetId);
      if (found) return found;
    }
  }

  return null;
}

export function toNodeId(item) {
  return typeof item === 'object' ? item.id : item;
}

export function collectLeafIds(node) {
  if (!node) return [];
  if (!node.children?.length) {
    return node.id ? [node.id] : [];
  }

  return node.children.flatMap(collectLeafIds);
}

function collectAllNodeIds(node) {
  if (!node || typeof node !== 'object') return [];
  const ids = node.id ? [node.id] : [];
  if (node.children?.length) {
    node.children.forEach((child) => {
      ids.push(...collectAllNodeIds(child));
    });
  }

  return ids;
}

export function findParentChapterForPageInToc(targetId, tocNodes) {
  if (!targetId || !Array.isArray(tocNodes)) return null;

  const search = (items, currentParentChapterId = null) => {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item?.id) {
        const nextParent = item.type === 'chapter' ? item.id : currentParentChapterId;
        if (item.id === targetId) {
          return item.type === 'chapter' ? item.id : currentParentChapterId;
        }
        if (item.children?.length) {
          const found = search(item.children, nextParent);
          if (found) return found;
        }
      }
    }

    return null;
  };

  return search(tocNodes);
}

export function filterIdsUnderChapter(ids, chapterId, toc) {
  return ids
    .map(toNodeId)
    .filter((id) => id && findParentChapterForPageInToc(id, toc) === chapterId);
}

/* --- Chapter refs & sections --- */

export function getChapterRefId(ch) {
  return ch?.chapterId || ch?.id;
}

export function getChapterTitleOrEmpty(chapterId, toc) {
  const title = findNodeById(toc, chapterId)?.title;

  return title ? [title] : [];
}

const sectionNum = (section) => {
  if (typeof section === 'string') return section;
  if (section?.num != null) return String(section.num);

  return '';
};

const isSubsectionNum = (section) => sectionNum(section).includes('.');

export function getStoredChapterTitles(storedChapters) {
  return storedChapters
    .filter((c) => getChapterRefId(c))
    .map((c) => c?.chapterTitle || c?.title || '')
    .filter(Boolean);
}

/* --- Dedupe pipeline --- */

function subtreeContainsTakenId(node, takenSectionIds) {
  if (!node || !takenSectionIds?.size) return false;

  return collectAllNodeIds(node).some((id) => takenSectionIds.has(id));
}

function registerSubtreeIds(node, takenSectionIds) {
  if (!node) return;
  collectAllNodeIds(node).forEach((id) => takenSectionIds.add(id));
}

function isPartialTextbookCoverage({ chapters, sectionIds, pages, sections }) {
  return sectionIds.length > 0
    || pages.length > 0
    || sections.some((section) => section != null && isSubsectionNum(section))
    || chapters.some((ch) => {
      const chapterId = getChapterRefId(ch);

      return ch?.selectedNodeId && chapterId && ch.selectedNodeId !== chapterId;
    });
}

function shouldKeepDedupedChapter(ch, takenChapterIds, takenSectionIds, safeToc) {
  const chapterId = getChapterRefId(ch);

  if (!chapterId) {
    return ch?.num != null && String(ch.num).trim() !== '';
  }

  if (takenChapterIds.has(chapterId)) return false;

  const chapterNode = findNodeById(safeToc, chapterId);
  if (!chapterNode) return true;

  return !subtreeContainsTakenId(chapterNode, takenSectionIds);
}

export function isFullyAssignedChapterOnRow(ch, sectionIds, safeToc) {
  const chapterId = getChapterRefId(ch);
  if (!chapterId) return false;
  if ((ch.sections || []).length > 0) return false;
  if (ch?.selectedNodeId && ch.selectedNodeId !== chapterId) return false;

  return filterIdsUnderChapter(sectionIds, chapterId, safeToc).length === 0;
}

function syncSectionsAfterSectionIdDedupe(sections, sectionIds, outSectionIds) {
  if (!sectionIds.length) return sections;

  const keptIdSet = new Set(outSectionIds);
  const filtered = [];
  sectionIds.forEach((id, idx) => {
    if (keptIdSet.has(id) && sections[idx] != null) {
      filtered.push(sections[idx]);
    }
  });

  return filtered.length > 0 ? filtered : null;
}



/* ------------------------------------------------------------------
 * Public API (exported — bundle + Utils delegates)
 * ------------------------------------------------------------------ */

/** Returns an array for coverage fields; null/undefined become []. */
export function asCoverageArray(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * Expands syllabus sectionIds to leaf slate page ids for publish payloads.
 * Module/chapter nodes with children expand to all descendant leaf ids.
 * @param {string[]} sectionIds
 * @param {Array|Object} tocData - TOC children array or asset with children
 * @returns {string[]}
 */
export function collectLeafPageIdsFromSectionIds(sectionIds, tocData) {
  if (!Array.isArray(sectionIds) || sectionIds.length === 0) return [];

  const safeToc = Array.isArray(tocData) ? tocData : (tocData?.children || []);
  const leafIds = [];
  const added = new Set();

  sectionIds.forEach((sectionId) => {
    const node = findNodeById(safeToc, sectionId);
    if (!node) return;

    const ids = node.children?.length ? collectLeafIds(node) : [node.id].filter(Boolean);
    ids.forEach((id) => {
      if (id && !added.has(id)) {
        leafIds.push(id);
        added.add(id);
      }
    });
  });

  return leafIds;
}

/**
 * First-wins assignment coverage dedupe, always keyed by TOC node id.
 * Any whole or partial overlap on a chapter drops that entire chapter from the row.
 * Rows are dropped only when no full or partial coverage remains after dedupe.
 */
export function dedupeAssignmentsCoverage(
  assignments,
  tocData,
  { restrictAlreadyAssigned = true, dropFullChapterDuplicates = true } = {}
) {
  if (!restrictAlreadyAssigned || !Array.isArray(assignments)) {
    return assignments || [];
  }

  const safeToc = Array.isArray(tocData) ? tocData : [];
  const takenChapterIds = new Set();
  const takenSectionIds = new Set();
  const result = [];

  assignments.forEach((assignment) => {
    const textbookCoverage = assignment?.textbookCoverage || {};
    const chapters = asCoverageArray(textbookCoverage.chapters);
    const sectionIds = asCoverageArray(textbookCoverage.sectionIds);
    const pages = asCoverageArray(textbookCoverage.pages);
    const sections = asCoverageArray(textbookCoverage.sections);

    if (chapters.length === 0 && sectionIds.length === 0 && pages.length === 0 && sections.length === 0) {
      if (assignment?.title) {
        result.push(assignment);
      }

      return;
    }

    const keptChapters = chapters.filter((ch) => shouldKeepDedupedChapter(
      ch, takenChapterIds, takenSectionIds, safeToc
    ));

    const blockedIds = new Set();
    if (sectionIds.length === 0) {
      chapters.forEach((ch) => {
        const chapterId = getChapterRefId(ch);
        if (!chapterId || shouldKeepDedupedChapter(ch, takenChapterIds, takenSectionIds, safeToc)) {
          return;
        }
        const chapterNode = findNodeById(safeToc, chapterId);
        if (chapterNode) {
          collectAllNodeIds(chapterNode).forEach((id) => blockedIds.add(id));
        }
      });
    }

    const isTaken = (id) => id && (takenSectionIds.has(id) || blockedIds.has(id));

    const nextSectionIds = [];
    sectionIds.forEach((sectionId) => {
      if (isTaken(sectionId)) return;
      takenSectionIds.add(sectionId);
      nextSectionIds.push(sectionId);
    });

    const isPartial = isPartialTextbookCoverage({ chapters, sectionIds, pages, sections });
    const allowedPageIds = !isPartial ? new Set() : null;
    if (allowedPageIds) {
      keptChapters.forEach((ch) => {
        const chapterNode = findNodeById(safeToc, getChapterRefId(ch));
        if (chapterNode) {
          collectAllNodeIds(chapterNode).forEach((id) => allowedPageIds.add(id));
        }
      });
    }

    const nextPages = [];
    pages.forEach((pageId) => {
      const id = toNodeId(pageId);
      if (!id || isTaken(id)) return;
      if (allowedPageIds && !allowedPageIds.has(id)) return;
      takenSectionIds.add(id);
      nextPages.push(pageId);
    });

    keptChapters.forEach((ch) => {
      const chapterId = getChapterRefId(ch);
      if (!chapterId) return;
      takenChapterIds.add(chapterId);
      if (isFullyAssignedChapterOnRow(ch, sectionIds, safeToc)) {
        registerSubtreeIds(findNodeById(safeToc, chapterId), takenSectionIds);
      }
    });

    const outSectionIds = isPartial ? nextSectionIds : sectionIds;
    let nextSections = sections.length > 0 ? sections : null;
    if (isPartial && sectionIds.length > 0) {
      nextSections = syncSectionsAfterSectionIdDedupe(sections, sectionIds, outSectionIds);
    }
    const nextCoverage = {
      ...textbookCoverage,
      chapters: keptChapters,
      sectionIds: outSectionIds,
      pages: nextPages,
      sections: nextSections
    };
    const hasCoverage = keptChapters.length > 0
      || outSectionIds.length > 0
      || nextPages.length > 0
      || asCoverageArray(nextSections).length > 0;
    const coverageWasAdjusted = keptChapters.length < chapters.length
      || nextPages.length < pages.length
      || (isPartial && nextSectionIds.length < sectionIds.length)
      || (isPartial && sectionIds.length > 0 && asCoverageArray(nextSections).length < sections.length);

    if (!hasCoverage) {
      if (!dropFullChapterDuplicates && assignment?.title) {
        result.push({
          ...assignment,
          textbookCoverage: {
            ...nextCoverage,
            chapters: [],
            sectionIds: [],
            pages: [],
            sections: []
          }
        });
      }

      return;
    }

    result.push({
      ...assignment,
      ...(coverageWasAdjusted && {
        coverageDedupeAction: COVERAGE_DEDUPE_ACTION.ADJUSTED
      }),
      textbookCoverage: nextCoverage
    });
  });

  return result;
}

/**
 * Dedupe syllabus store schedule after TOC mapping (parent one-reader path).
 */
export function applyCoverageDedupeToSyllabusData(syllabusData, tocAsset) {
  const schedule = syllabusData?.syllabusInfo?.data?.schedule;
  if (!Array.isArray(schedule) || schedule.length === 0) {
    return syllabusData;
  }

  const tocData = tocAsset?.children || (Array.isArray(tocAsset) ? tocAsset : []);
  const dedupedSchedule = dedupeAssignmentsCoverage(schedule, tocData);
  const updated = JSON.parse(JSON.stringify(syllabusData));
  updated.syllabusInfo.data.schedule = dedupedSchedule;

  if (updated.syllabusInfo.assignments?.length) {
    updated.syllabusInfo.assignments = updated.syllabusInfo.assignments.map((assignment, index) => {
      const scheduleItem = assignment.referenceId
        ? dedupedSchedule.find((item) => item.referenceId === assignment.referenceId)
        : dedupedSchedule[index];
      if (!scheduleItem) return assignment;

      return {
        ...assignment,
        textbookCoverage: scheduleItem.textbookCoverage,
        coverageDedupeAction: scheduleItem.coverageDedupeAction ?? assignment.coverageDedupeAction ?? null
      };
    });
  }

  return updated;
}

export function validateAssignmentChapters(assignments, tocData, restrictAlreadyAssigned = true) {
  return dedupeAssignmentsCoverage(assignments, tocData, {
    restrictAlreadyAssigned,
    dropFullChapterDuplicates: true
  });
}

export function dedupeAssignmentSectionIds(assignments, restrictAlreadyAssigned = true, tocData = []) {
  return dedupeAssignmentsCoverage(assignments, tocData, {
    restrictAlreadyAssigned,
    dropFullChapterDuplicates: false
  });
}
